// @ts-nocheck
/**
 * LangPanel — inline scrollable language list for the hamburger flyout.
 *
 * Used inside WebViewNavBar's menu flyout at <640px. Renders language options
 * as plain buttons (no <select>) so the list never clips off-screen.
 *
 * Props:
 *   sites         — Array<{ siteId, locale, name }>
 *   currentSiteId — currently selected siteId string
 *   onSelect      — called with siteId string when an option is chosen
 *   maxHeight     — CSS string for the scrollable area max-height (default "40vh")
 */
import React, { useCallback } from "react";

const font = "'Open Sans', system-ui, sans-serif";

// ── Icons ──────────────────────────────────────────────────────────────────

const CheckIcon = () => (
  <svg
    width="13" height="13" viewBox="0 0 24 24"
    fill="none" stroke="currentColor" strokeWidth="2.5"
    style={{ display: "block", flexShrink: 0 }}
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const GoogleTranslateIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="#4285F4"
    style={{ flexShrink: 0, display: "block" }}>
    <path d="M12.87 15.07l-2.54-2.51.03-.03A17.52 17.52 0 0 0 14.07 6H17V4h-7V2H8v2H1v2h11.17C11.5 7.92 10.44 9.75 9 11.35 8.07 10.32 7.3 9.19 6.69 8h-2c.73 1.63 1.73 3.17 2.98 4.56l-5.09 5.02L4 19l5-5 3.11 3.11.76-2.04zM18.5 10h-2L12 22h2l1.12-3h4.75L21 22h2l-4.5-12zm-2.62 7l1.62-4.33L19.12 17h-3.24z" />
  </svg>
);

// ── LangPanel ──────────────────────────────────────────────────────────────

export default function LangPanel({
  sites = [],
  currentSiteId,
  onSelect,
  maxHeight = "40vh",
}) {
  const handleSelect = useCallback((siteId) => {
    if (onSelect) onSelect(siteId);
  }, [onSelect]);

  if (!sites.length) return null;

  return (
    <div style={panelStyle}>
      <ul style={{ ...listStyle, maxHeight }} role="listbox" aria-label="Language">
        {sites.map(site => {
          const isActive = String(site.siteId) === String(currentSiteId);
          return (
            <li key={site.siteId} role="option" aria-selected={isActive} style={itemStyle}>
              <button
                type="button"
                onClick={() => handleSelect(site.siteId)}
                onMouseDown={e => e.preventDefault()}
                style={{
                  ...rowStyle,
                  background: isActive ? "#e8f0fe" : "transparent",
                  color: isActive ? "#0055aa" : "#333",
                  fontWeight: isActive ? 700 : 400,
                }}
              >
                {/* Language icon — shows on active row, spacer on others */}
                <span style={{ width: 14, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {isActive
                    ? <CheckIcon />
                    : <span style={{ width: 13, display: "block" }} />}
                </span>

                {/* Language name */}
                <span style={nameStyle}>{site.name}</span>

                {/* Locale code badge */}
                <span style={{
                  ...localeBadgeStyle,
                  background: isActive ? "rgba(0,85,170,0.12)" : "rgba(0,0,0,0.06)",
                  color: isActive ? "#0055aa" : "#666",
                }}>
                  {site.locale}
                </span>

                {/* Translate icon */}
                <GoogleTranslateIcon size={13} />
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ── Styles ─────────────────────────────────────────────────────────────────

const panelStyle = {
  background: "#fff",
  fontFamily: font,
  overflowY: "auto",
  scrollbarWidth: "thin",
  scrollbarColor: "rgba(0,102,204,0.25) transparent",
};

const listStyle = {
  listStyle: "none",
  margin: 0,
  padding: "4px",
  overflowY: "auto",
  scrollbarWidth: "thin",
  scrollbarColor: "rgba(0,102,204,0.25) transparent",
};

const itemStyle = {
  borderRadius: "6px",
  overflow: "hidden",
};

const rowStyle = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  width: "100%",
  padding: "8px 10px",
  border: "none",
  cursor: "pointer",
  borderRadius: "6px",
  textAlign: "left",
  fontFamily: font,
  fontSize: "0.84rem",
  lineHeight: 1.3,
  transition: "background 0.12s ease",
};

const nameStyle = {
  flex: 1,
  wordBreak: "break-word",
};

const localeBadgeStyle = {
  fontSize: "0.7rem",
  fontWeight: 600,
  padding: "1px 5px",
  borderRadius: "3px",
  flexShrink: 0,
  letterSpacing: "0.03em",
  fontFamily: font,
};
